(() => {
  if (customElements.get('bf-spend-save')) return;
  class SpendSave extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.tasks = new Map();
      this.requests = new Map();
      const signal = this.abort.signal;
      queueMicrotask(() => {
        if (!this.isConnected || signal.aborted) return;
        this.desktop = window.matchMedia('(min-width: 750px)');
        this.home = this.querySelector('[data-promo-home]');
        this.promo = this.querySelector('[data-spend-promo]');
        this.categories = [...this.querySelectorAll('[data-spend-category]')].map(node => ({
          node, tab: node.querySelector('[data-spend-tab]'), panel: node.querySelector('[data-spend-panel]'),
          track: node.querySelector('[data-spend-track]'), slot: node.querySelector('[data-promo-slot]'),
          slider: node.querySelector('swiper-slider'), products: [...node.querySelectorAll('[data-spend-product]')]
        }));
        this.active = 0;
        this.limit = null;
        const nav = this.querySelector('[data-spend-nav]');
        this.categories.forEach((category, index) => {
          nav.append(category.tab);
          category.tab.addEventListener('click', () => this.activateCategory(index), { signal });
          category.tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % this.categories.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + this.categories.length) % this.categories.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.categories.length - 1;
            if (next !== undefined) { event.preventDefault(); this.activateCategory(next); this.categories[next].tab.focus(); }
          }, { signal });
          const viewport = category.track.closest('[data-swiper]');
          const update = () => this.updateNavigation(category);
          viewport.addEventListener('scroll', update, { signal, passive: true });
          category.slider.addEventListener('swiper:init', () => {
            ['progress', 'resize', 'reachBeginning', 'reachEnd', 'fromEdge', 'slideChange'].forEach(eventName => {
              category.slider.slider?.on(eventName, update);
            });
            update();
          }, { signal });
          [['[data-spend-prev]', -1], ['[data-spend-next]', 1]].forEach(([selector, direction]) => {
            category.panel.querySelector(selector).addEventListener('click', () => {
              const button = category.panel.querySelector(selector);
              if (button.disabled) return;
              const swiper = category.slider.slider;
              if (swiper) {
                const before = swiper.translate;
                direction < 0 ? swiper.slidePrev() : swiper.slideNext();
                window.setTimeout(() => {
                  if (swiper.translate === before) { button.disabled = true; return; }
                  update();
                }, 360);
              } else viewport.scrollBy({ left: direction * viewport.clientWidth * .8, behavior: 'smooth' });
            }, { signal });
          });
          category.products.forEach(card => {
            const form = card.querySelector('product-form-component');
            if (!form) return;
            form.addEventListener('cart:update', event => {
              if (event.detail?.data?.source === 'product-form-component' && !event.detail.data.didError) this.finish(card, 'success', this.dataset.added);
            }, { signal });
            form.addEventListener('cart:error', event => this.finish(card, 'error', event.detail?.data?.message || this.dataset.failed), { signal });
            if (card.dataset.cartState === 'loading') this.finish(card, 'error', this.dataset.unconfirmed);
            else if (card.dataset.cartState) delete card.dataset.cartState;
          });
        });
        this.querySelectorAll('[data-price-limit]').forEach(button => button.addEventListener('click', () => {
          this.limit = button.dataset.priceLimit === '' ? null : Number(button.dataset.priceLimit);
          this.render();
        }, { signal }));
        this.querySelector('[data-reset-price]').addEventListener('click', () => { this.limit = null; this.render(); }, { signal });
        this.desktop.addEventListener('change', () => this.render(), { signal });
        this.addEventListener('submit', event => {
          const card = event.target.closest('[data-spend-product]');
          if (!card || event.target.tagName !== 'FORM') return;
          if (card.dataset.available !== 'true' || card.dataset.cartState === 'loading') { event.preventDefault(); event.stopImmediatePropagation(); return; }
          this.clearTask(card);
          card.dataset.cartState = 'loading';
          this.syncButton(card);
          card.querySelector('[data-spend-status]').textContent = this.dataset.loading;
          this.tasks.set(card, setTimeout(() => this.finish(card, 'error', this.dataset.unconfirmed), 15000));
        }, { signal, capture: true });
        if (window.Shopify?.designMode) document.addEventListener('shopify:block:select', event => {
          const index = this.categories.findIndex(category => category.node.contains(event.target) || category.tab.contains(event.target));
          if (index >= 0) this.activateCategory(index);
        }, { signal });
        this.dataset.enhanced = 'true';
        this.render();
        customElements.whenDefined('swiper-slider').then(() => {
          if (this.isConnected && !signal.aborted) this.refreshSlider(this.categories[this.active]);
        });
      });
    }
    activateCategory(index) {
      if (index !== this.active) this.limit = null;
      this.active = index;
      this.render();
    }
    render() {
      const category = this.categories?.[this.active];
      if (!category) { this.querySelector('[data-spend-empty]').hidden = false; return; }
      this.categories.forEach((item, index) => {
        item.panel.hidden = index !== this.active;
        item.tab.setAttribute('aria-selected', String(index === this.active));
        item.tab.tabIndex = index === this.active ? 0 : -1;
      });
      this.querySelectorAll('[data-price-limit]').forEach(button => button.setAttribute('aria-pressed', String((button.dataset.priceLimit === '' ? null : Number(button.dataset.priceLimit)) === this.limit)));
      const products = category.products.filter(card => this.limit === null || Number(card.dataset.price) < this.limit);
      if (this.promo) {
        const artwork = category.node.querySelector('[data-category-artwork]')?.content.querySelector('img')
          || this.promo.querySelector('[data-default-artwork]')?.content.querySelector('img');
        const current = this.promo.querySelector(':scope > img');
        if (artwork) {
          if (!current || current.getAttribute('src') !== artwork.getAttribute('src')) {
            current?.remove();
            this.promo.prepend(artwork.cloneNode(true));
          }
        } else current?.remove();
      }
      if (this.promo) category.slot.append(this.promo);
      const children = [...(this.promo ? [category.slot] : []), ...products];
      const trackChanged = children.length !== category.track.children.length
        || children.some((child, index) => category.track.children[index] !== child);
      if (trackChanged) {
        this.destroySlider(category);
        category.track.replaceChildren(...children);
      }
      this.querySelector('[data-spend-empty]').hidden = products.length > 0;
      this.refreshSlider(category, trackChanged);
      const viewport = category.track.closest('[data-swiper]');
      if (trackChanged && viewport) viewport.scrollLeft = 0;
      this.updateNavigation(category);
    }
    destroySlider(category) {
      const slider = category.slider?.slider;
      if (!slider) return;
      slider.destroy(true, true);
      category.slider.slider = null;
      category.slider.initialized = false;
    }
    refreshSlider(category, resetPosition = false) {
      const slider = category?.slider;
      if (!slider) return;
      slider.slides = category.track.querySelectorAll('[data-swiper-slide], .swiper-slide');
      if (slider.slider) {
        slider.slider.update();
        if (resetPosition) slider.slider.slideTo(0, 0);
      } else if (slider.initializeSwiper && slider.slides.length) slider.initializeSwiper();
    }
    updateNavigation(category) {
      const swiper = category.slider?.slider;
      const viewport = category.track.closest('[data-swiper]');
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const locked = swiper
        ? swiper.isLocked || Math.abs(swiper.maxTranslate() - swiper.minTranslate()) <= 1
        : max <= 1;
      const progressTrack = category.panel.querySelector('.bf-spend__progress');
      const arrows = category.panel.querySelector('.bf-spend__arrows');
      progressTrack.hidden = locked;
      arrows.hidden = locked;
      const progress = swiper ? Math.max(0, Math.min(1, swiper.progress || 0)) : (max ? viewport.scrollLeft / max : 0);
      const atEnd = swiper ? swiper.isLocked || swiper.isEnd || swiper.progress >= 0.995 || Math.abs(swiper.translate - swiper.maxTranslate()) <= 1 : viewport.scrollLeft >= max - 1;
      const start = swiper ? swiper.isBeginning || swiper.isLocked : viewport.scrollLeft <= 1;
      const end = atEnd;
      category.panel.querySelector('[data-spend-prev]').disabled = Boolean(start);
      category.panel.querySelector('[data-spend-next]').disabled = Boolean(end);
      const fraction = swiper ? Math.min(1, 1 / Math.max(1, swiper.snapGrid?.length || 1)) : (viewport.scrollWidth ? Math.min(1, viewport.clientWidth / viewport.scrollWidth) : 1);
      const bar = category.panel.querySelector('[data-spend-progress]');
      bar.style.width = (fraction * 100) + '%';
      bar.style.marginInlineStart = ((1 - fraction) * progress * 100) + '%';
    }
    syncButton(card) {
      const button = card.querySelector('[name="add"]');
      if (!button) return;
      const loading = card.dataset.cartState === 'loading';
      button.disabled = loading || card.dataset.available !== 'true';
      button.setAttribute('aria-busy', String(loading));
    }
    clearTask(card) { clearTimeout(this.tasks.get(card)); this.tasks.delete(card); }
    finish(card, state, message) {
      this.clearTask(card);
      card.dataset.cartState = state;
      this.syncButton(card);
      card.querySelector('[data-spend-status]').textContent = message;
      this.refreshAvailability(card);
      if (state === 'success') this.tasks.set(card, setTimeout(() => {
        delete card.dataset.cartState;
        this.syncButton(card);
        card.querySelector('[data-spend-status]').textContent = '';
        this.tasks.delete(card);
      }, 2500));
    }
    async refreshAvailability(card) {
      if (!this.isConnected || this.abort.signal.aborted) return;
      const form = card.querySelector('product-form-component');
      const variantId = card.querySelector('[name="id"]')?.value;
      if (!form?.dataset.productUrl || !variantId) return;
      const signal = this.abort.signal, requests = this.requests;
      const url = new URL(form.dataset.productUrl, window.location.origin);
      if (url.origin !== window.location.origin) return;
      url.pathname += '.js';
      url.search = '';
      try {
        let request = requests.get(url.href);
        if (!request) {
          request = fetch(url.href, { signal, headers: { Accept: 'application/json' } }).then(response => response.ok ? response.json() : null);
          requests.set(url.href, request);
          request.finally(() => requests.delete(url.href)).catch(() => {});
        }
        const product = await request;
        if (signal.aborted || !this.isConnected) return;
        const variant = product?.variants?.find(item => String(item.id) === String(variantId));
        if (!variant) return;
        this.categories.forEach(category => category.products.forEach(item => {
          if (item.querySelector('[name="id"]')?.value === variantId) { item.dataset.available = String(variant.available); this.syncButton(item); }
        }));
      } catch {}
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.tasks?.forEach(timer => clearTimeout(timer));
      this.tasks?.clear();
      if (this.home && this.promo) this.home.append(this.promo);
      this.categories?.forEach(category => {
        category.node.append(category.tab);
        category.track.replaceChildren(category.slot, ...category.products);
        category.panel.hidden = false;
        if (category.slider.slider) { category.slider.slider.destroy(true, true); category.slider.slider = null; category.slider.initialized = false; }
      });
    }
  }
  customElements.define('bf-spend-save', SpendSave);
})();
